import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styled from "@emotion/styled";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import Modal from "../Modal/Modal";
import AiSidebar from "../AISidebar/AISidebar";
import { pulse } from "../AISidebar/SidebarMessages/SidebarLoader";
import formatter from "../../utils/formatters/formatter";
import ExportButton from "../ExportButton/ExportButton";
import Flex from "../Flex/Flex";
import ResultHistoryHeader from "./ResultHistoryHeader";
import {
  selectActiveResult,
  selectResultsHistory,
} from "../../store/selectors/aiResults";
import { aiSetActiveResultMessageId } from "../../store/actions/dashboard/dashboard";

const ModalBody = styled.div`
  display: flex;
  height: 80vh;
  overflow: hidden;
`;

const TablePanel = styled.div`
  flex: 0 0 55%;
  overflow: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ChatPanel = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 0 16px 16px;
`;

const Divider = styled.div`
  width: 1px;
  background: ${({ theme }) => theme.divider};
  flex-shrink: 0;
`;

const LoadingPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 16px;
`;

const LoadingDot = styled.div`
  width: 12px;
  height: 12px;
  background: linear-gradient(135deg, #5b6bfd, #8a94ff);
  border-radius: 50%;
  animation: ${pulse} 2s infinite;
  animation-delay: ${({ delay }) => delay || "0s"};
`;

const DotsRow = styled.div`
  display: flex;
  gap: 8px;
`;

const LoadingText = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text.secondary};
`;

const OpenInExplorerLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.primary};
  font-size: 13px;
  cursor: pointer;
  padding: 8px 0;
  margin-top: 12px;
  flex-shrink: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const SimpleTableWrapper = styled.div`
  overflow-x: auto;
  min-width: 0;
  flex: 1;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    position: sticky;
    top: 0;
    background: ${({ theme }) =>
      theme.type === "light" ? theme.gray.gray100 : theme.gray.gray800};
    color: ${({ theme }) => theme.text.secondary};
    font-weight: 600;
    text-align: left;
    padding: 8px 12px;
    white-space: nowrap;
    border-bottom: 2px solid ${({ theme }) => theme.divider};
  }

  td {
    padding: 8px 12px;
    white-space: nowrap;
    border-bottom: 1px solid ${({ theme }) => theme.divider};
    color: ${({ theme }) => theme.text.primary};
  }

  tbody tr:hover {
    background: ${({ theme }) =>
      theme.type === "light"
        ? theme.gray.gray100
        : "rgba(255, 255, 255, 0.03)"};
  }
`;

const SortableTh = styled.th`
  cursor: pointer;
  user-select: none;
  text-align: ${({ align }) => align};

  &:hover {
    color: ${({ theme }) => theme.text.primary};
  }
`;

const SortIconWrapper = styled.span`
  display: inline-flex;
  margin: 0 6px;
  opacity: 0.8;
`;

const NUMERIC_TYPES = ["currency", "decimal", "float", "number", "integer"];
const DATE_TYPES = ["date", "datetime", "timestamp"];

function compareValues(a, b, type, dir) {
  const mul = dir === "ASC" ? 1 : -1;
  if (a == null) return 1;
  if (b == null) return -1;
  if (NUMERIC_TYPES.includes(type)) return (Number(a) - Number(b)) * mul;
  if (DATE_TYPES.includes(type)) return (new Date(a) - new Date(b)) * mul;
  return String(a).localeCompare(String(b)) * mul;
}

function SimpleTable({ data, meta, sort, onSort }) {
  const columns = useMemo(
    () => (data?.length ? Object.keys(data[0]) : []),
    [data]
  );

  const getField = (colName) => meta?.fields?.find((f) => f.name === colName);

  const sortedData = useMemo(() => {
    if (!sort || !data?.length) return data;
    const type = getField(sort.name)?.type || "string";
    return [...data].sort((r1, r2) =>
      compareValues(r1[sort.name], r2[sort.name], type, sort.direction)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sort, meta]);

  if (!data?.length) {
    return <em style={{ color: "#999" }}>(No results)</em>;
  }

  const getLabel = (colName) => getField(colName)?.label || colName;

  const formatCell = (value, colName) => {
    const type = getField(colName)?.type || "string";
    return formatter(value, type);
  };

  const getAlign = (colName) =>
    NUMERIC_TYPES.includes(getField(colName)?.type) ? "right" : "left";

  return (
    <StyledTable>
      <thead>
        <tr>
          {columns.map((col) => {
            const align = getAlign(col);
            const isSorted = sort?.name === col;
            return (
              <SortableTh
                key={col}
                align={align}
                onClick={() => onSort(col)}
                title="Click to sort"
              >
                {getLabel(col)}
                <SortIcon direction={sort?.direction} visible={isSorted} />
              </SortableTh>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {sortedData.map((row, i) => (
          <tr key={i}>
            {columns.map((col) => (
              <td key={col} style={{ textAlign: getAlign(col) }}>
                {formatCell(row[col], col)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </StyledTable>
  );
}

function SortIcon({ direction, visible }) {
  return (
    <SortIconWrapper style={{ visibility: visible ? "visible" : "hidden" }}>
      <FontAwesomeIcon
        icon={["fal", direction === "DESC" ? "arrow-down" : "arrow-up"]}
        size="xs"
      />
    </SortIconWrapper>
  );
}

export default function AIResultsModal({
  onClose,
  onSendFollowUpQuestion,
  onStop,
  onOpenInExplorer,
}) {
  const followUpLoading = useSelector(
    (state) => state.dashboard.aiSidebar.followUpLoading
  );
  const activeResult = useSelector(selectActiveResult);
  const resultsHistory = useSelector(selectResultsHistory);
  const dispatch = useDispatch();
  const history = useHistory();
  const [question, setQuestion] = useState("");
  const [sort, setSort] = useState(null);

  const activeMessageId = activeResult?.messageId ?? null;
  useEffect(() => {
    setSort(null);
  }, [activeMessageId]);

  const handleSort = (name) =>
    setSort((prev) =>
      prev?.name === name
        ? { name, direction: prev.direction === "ASC" ? "DESC" : "ASC" }
        : { name, direction: "ASC" }
    );

  const handleOpenInExplorer = () => {
    history.push("/admin/data-settings/data-explorer/ai-results");
    if (onOpenInExplorer) {
      onOpenInExplorer();
    }
  };

  const handleSelectResult = (messageId) =>
    dispatch(aiSetActiveResultMessageId(messageId));

  return (
    <Modal
      renderInPortal
      controlledFromOutside
      overrideShow={true}
      close={onClose}
      width="85vw"
      showClose
      xPadding={0}
    >
      <ModalBody>
        <TablePanel>
          {activeResult ? (
            <>
              {resultsHistory.length >= 1 ? <ResultHistoryHeader /> : null}
              <SimpleTableWrapper>
                <SimpleTable
                  data={activeResult.data}
                  meta={activeResult.meta}
                  sort={sort}
                  onSort={handleSort}
                />
              </SimpleTableWrapper>
              <Flex justifyContent="space-between" alignItems="center">
                <OpenInExplorerLink onClick={handleOpenInExplorer}>
                  Open in Data Explorer &rarr;
                </OpenInExplorerLink>
                <ExportButton
                  data={activeResult.data}
                  fields={activeResult.meta?.fields ?? []}
                  fileName="AI Results"
                />
              </Flex>
            </>
          ) : followUpLoading ? (
            <LoadingPlaceholder>
              <DotsRow>
                <LoadingDot delay="0s" />
                <LoadingDot delay="0.3s" />
                <LoadingDot delay="0.6s" />
              </DotsRow>
              <LoadingText>Generating results...</LoadingText>
            </LoadingPlaceholder>
          ) : (
            <LoadingPlaceholder>
              <LoadingText>Ask a question to generate results.</LoadingText>
            </LoadingPlaceholder>
          )}
        </TablePanel>
        <Divider />
        <ChatPanel>
          <AiSidebar
            embedded
            isOpen={true}
            onClose={onClose}
            onSendFollowUpQuestion={onSendFollowUpQuestion}
            onStop={onStop}
            onSelectResult={handleSelectResult}
            liftedStateObject={{
              questionState: [question, setQuestion],
              chartImageState: [],
              widthState: [],
            }}
          />
        </ChatPanel>
      </ModalBody>
    </Modal>
  );
}
